<template>
  <div class="bg-light min-vh-100">
    <div class="p-5">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h1 class="font-weight-bold">Saved Images</h1>
        <router-link
          :to="{ name: 'business.image-editor' }"
          class="btn btn-primary"
        >
          Create New Image
        </router-link>
      </div>
      <div>
        <div style="font-family: sans-serif; line-height: 1.6; padding: 1rem; background: #f9f9f9; border: 1px solid #ddd; border-radius: 8px;">
          <h5 style="margin-top: 0;">💡 About Personalized Images</h5>
          <p>
            Personalized Images help you boost review conversions by adding a face to the service. When a customer sees the employee they worked with in the review request, they’re more likely to respond.
          </p>
          <p>
            Upload or generate a branded image for each employee and assign it below. These images will be automatically used in your review emails, texts, and social media posts.
          </p>
          <p style="margin-top: 1rem;"><strong>👉 Pro Tip:</strong> Use clear, high-quality photos with your logo or vehicle for maximum impact.</p>
        </div>

      </div>
      <!-- No saved images message -->
      <div v-if="!hasLoaded && !savedImages.length" class="text-center pt-4">
        <b-spinner large />
      </div>
      <div v-else-if="!savedImages.length" class="text-center py-5 bg-white rounded shadow">
        <b-icon-image class="h3 text-muted mb-3" style="width: 4rem; height: 4rem;"></b-icon-image>
        <h3 class="h5 font-weight-medium">No saved images</h3>
        <p class="text-muted">Get started by creating your first image</p>
        <div class="mt-3">
          <router-link
            :to="{ name: 'business.image-editor' }"
            class="btn btn-primary"
          >
            Create New Image
          </router-link>
        </div>
      </div>

      <!-- Image Grid -->
      <b-row v-else class="mt-4">
        <b-col
          v-for="image in savedImages"
          :key="image.id"
          cols="12" sm="6" lg="4"
          class="mb-4"
        >
          <b-card no-body class="h-100 shadow-sm">
            <!-- Image Info -->
            <b-card-body class="pb-0">
              <div class="d-flex">
                <div class="flex-grow-1">
                  <h3 class="h5 font-weight-medium mb-1">{{ image.name }}</h3>
                  <p class="small text-muted mb-0">
                    Last updated: {{ formatDate(image.updated_at) }}
                  </p>
                  <div class="font-size-12">
                    <p v-if="image.employee">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user-check mr-1 text-blue-500"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><polyline points="16 11 18 13 22 9"></polyline></svg>
                      <span>Assigned to: {{image.employee.name}}</span>
                    </p>
                    <p v-else>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users mr-1 text-gray-400"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                      <span>Not Assigned</span>
                    </p>
                  </div>
                </div>
                <div>
                  <b-badge
                      v-if="image.default"
                      variant="success"
                      class="rounded-lg"
                  >
                    <b-icon-check-circle class="mr-2"></b-icon-check-circle> Default
                  </b-badge>
                  <b-button v-else variant="link" class="text-dark-blue pr-0 text-decoration-none" @click="setDefaultSavedImage(image)">
                    <b-spinner v-if="image.defaultWorking" small />
                    <b-icon-bookmark v-else class="mr-1"></b-icon-bookmark> Default
                  </b-button>
                </div>
              </div>
              <!-- Image Preview -->
              <div class="rounded">
                <b-img
                    :src="image.url"
                    :alt="image.name"
                    class="w-100 h-100 rounded"
                    style="object-fit: cover;"
                ></b-img>
                <b-badge
                    v-if="image.isDefault"
                    variant="success"
                    style="position: absolute; top: 10px; right: 10px;"
                >
                  <b-icon-check-circle class="mr-2" /> Default
                </b-badge>
              </div>
              <div class="card-foot mt-2">
                <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start">
                  <!-- Left Group of Buttons -->
                  <div class="d-flex flex-wrap justify-content-start">
                    <b-button
                        v-b-tooltip.hover
                        variant="light"
                        size="sm"
                        class="mr-2 mb-2"
                        title="Edit"
                        @click="handleEdit(image)"
                    >
                      <b-icon-pencil-square class="mr-1"></b-icon-pencil-square> Edit
                    </b-button>

                    <b-button
                        v-b-tooltip.hover
                        variant="light"
                        size="sm"
                        class="mr-2 mb-2"
                        title="Rename"
                        @click="handleRename(image)"
                    >
                      <b-icon-pencil class="mr-1"></b-icon-pencil> Rename
                    </b-button>

                    <b-button
                        v-b-tooltip.hover
                        variant="light"
                        size="sm"
                        class="mr-2 mb-2"
                        title="Assign Employee"
                        @click="handleAssign(image)"
                    >
                      <b-spinner v-if="image.isWorking" small />
                      <i v-else class="uil uil-users-alt" /> Assign
                    </b-button>

                  </div>

                  <!-- Right (Delete) Button -->
                  <div class="mt-2 mt-sm-0">
                    <b-button
                        v-b-tooltip.hover
                        variant="outline-danger"
                        size="sm"
                        title="Delete this image"
                        @click="handleDelete(image)"
                    >
                      <b-spinner v-if="image.isDeleting" small />
                      <b-icon-trash v-else></b-icon-trash>
                    </b-button>
                  </div>
                </div>

              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Rename Modal -->
      <b-modal
        v-model="showRenameModal"
        title="Rename Image"
        header-bg-variant="light"
        ok-title="Save"
        ok-variant="primary"
        cancel-variant="outline-dark"
        @ok="saveNewName"
        @hidden="closeRenameModal"
      >
        <b-form-group>
          <b-form-input
            v-model="newImageName"
            placeholder="Enter new name"
            required
          ></b-form-input>
        </b-form-group>
      </b-modal>

      <!-- Delete Confirmation Modal -->
      <b-modal
        v-model="showDeleteModal"
        title="Confirm Deletion"
        header-bg-variant="light"
        ok-title="Delete"
        ok-variant="danger"
        cancel-variant="outline-dark"
        @ok="confirmDelete"
        @hidden="closeDeleteModal"
      >
        <p class="mb-0">Are you sure you want to delete this image? This action cannot be undone.</p>
      </b-modal>

      <!-- Employee Assign Modal -->
      <b-modal
          v-model="showAssignModal"
          title="Assign to Employee"
          header-bg-variant="light"
          ok-title="Save"
          ok-variant="primary"
          cancel-variant="outline-dark"
          @ok="confirmAssign"
          @hidden="closeAssignModal"
      >
        <p v-if="selectedImage" class="mb-0">
          <span class="d-block">Choose an employee to assign this image to.</span>
          <select v-model="selectedImage.employee_id" class="form-control">
            <option value="">No Employee (Unassigned)</option>
            <option v-for="employee in employees" :key="employee.id" :value="employee.id">
              {{ employee.name }}
            </option>
          </select>
        </p>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import {
  BRow, BCol, BCard, BCardBody, BImg,
  BBadge, BButton, BModal, BFormGroup, BFormInput, BIconBookmark,
  BIconImage, BIconPencil, BIconPencilSquare, BIconTrash, BIconCheckCircle
} from 'bootstrap-vue';
import Vue from "vue";

export default {
  name: 'Dashboard',
  components: {
    BRow, BCol, BCard, BCardBody, BImg,
    BBadge, BButton, BModal, BFormGroup, BFormInput, BIconBookmark,
    BIconImage, BIconPencil, BIconPencilSquare, BIconTrash, BIconCheckCircle
  },
  data() {
    return {
      showRenameModal: false,
      showDeleteModal: false,
      showAssignModal: false,
      selectedImage: null,
      newImageName: ''
    };
  },
  computed: {
    ...mapState('imageEditor', ['savedImages','hasLoaded']),
    user() {
      return this.$store.getters['auth/user']
    },
    employees(){
      return this?.user?.business?.employees;
    }
  },
  mounted() {
    this.fetchSavedImages(this.user.business.id);
  },
  methods: {
    ...mapActions('imageEditor', ['fetchSavedImages', 'deleteSavedImage', 'setDefaultSavedImage','assignEmployee']),
    ...mapMutations('imageEditor', ['SET_EDITING_IMAGE_ID']),

    formatDate(dateString) {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true
      }).format(date);
    },
    handleAssign(image){
      this.selectedImage = image;
      this.showAssignModal = true;
    },
    handleRename(image) {
      this.selectedImage = image;
      this.newImageName = image.name;
      this.showRenameModal = true;
    },

    closeRenameModal() {
      this.showRenameModal = false;
      this.selectedImage = null;
      this.newImageName = '';
    },

    async saveNewName() {
      if (!this.newImageName.trim()) {
        return;
      }

      try {
        const payload = {
          business_id: this.user.business.id,
          image_id: this.selectedImage.id,
          name: this.newImageName
        };
        this.$store
            .dispatch('imageEditor/saveImageName', payload)
            .then(() => {
              // Refresh the list
              this.fetchSavedImages(this.user.business.id).then(() => {
                this.closeRenameModal();
              });
            })
            .catch((err) => {
              console.log(err);
              Vue.$toast.open({
                message: err.response.data.errors,
                type: 'error',
                duration: 5000,
              })
            })
      } catch (error) {
        console.error('Error updating image name:', error);
        Vue.$toast.open({
          message: 'Failed to update image name',
          type: 'error',
          duration: 5000,
        })
      }
    },

    handleEdit(image) {
      this.SET_EDITING_IMAGE_ID(image.id);
      this.$router.push({ name: 'business.image-editor', params: { id: image.id } });
    },

    handleDelete(image) {
      if(image.default){
        Vue.$toast.open({
          message: 'You cannot delete the default image',
          type: 'error',
          duration: 5000,
        })
        return;
      }
      this.selectedImage = image;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.selectedImage = null;
    },

    closeAssignModal(){
      this.showAssignModal = false;
      this.selectedImage = null;
    },
    async confirmAssign(){
      if (this.selectedImage) {
        this.selectedImage.isWorking = true;
        try {
          await this.assignEmployee(this.selectedImage);
          this.closeAssignModal();
        } catch (error) {
          console.error('Error Assigning Image', error);
          Vue.$toast.open({
            message: 'Error Assigning Image',
            type: 'error',
            duration: 5000,
          })
        }
      }
    },
    async confirmDelete() {
      if (this.selectedImage) {
        this.selectedImage.isDeleting = true;
        try {
          await this.deleteSavedImage({businessId: this.user.business.id, imageId: this.selectedImage.id});
          this.closeDeleteModal();
          Vue.$toast.open({
            message: 'Image Successfully Deleted',
            type: 'success',
            duration: 5000,
          })
        } catch (error) {
          this.selectedImage.isDeleting = false
          console.error('Error deleting image:', error);
          Vue.$toast.open({
            message: 'Error Deleting Image',
            type: 'error',
            duration: 5000,
          })
        }
      }
    },

    async handleSetDefault(image) {
      try {
        await this.setDefaultSavedImage(image.id);
        this.$bvToast.toast(`${image.name} set as default image`, {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error setting default image:', error);
        this.$bvToast.toast('Failed to set default image', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    }
  }
};
</script>

<style scoped>

</style>
